import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavigationConfirmation } from '../components/confirmation-popup/navigationConfirmation';

// Generic warning message used across all pages
const GENERIC_WARNING_MESSAGE = "You have active work in progress. Are you sure you want to leave? Your progress will be lost.";

interface NavigationWarningOptions {
  hasUnsavedWork: boolean;
  onNavigationAttempt?: () => void;
}

/**
 * Hook to prevent accidental navigation away from pages with active work
 * Handles browser reload, back button, and internal React Router navigation
 * Uses a generic warning message across all pages
 */
export const useNavigationWarning = ({
  hasUnsavedWork,
  onNavigationAttempt
}: NavigationWarningOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = useRef(location.pathname);
  const isNavigatingRef = useRef(false);

  // Update current path when location changes
  useEffect(() => {
    currentPath.current = location.pathname;
  }, [location.pathname]);

  // Handle browser beforeunload event (reload, close tab, external navigation)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedWork && !isNavigatingRef.current && !isNavigatingAfterConfirmation) {
        // Modern browsers ignore custom messages and show their own dialog
        // We just need to set returnValue to trigger the browser's native dialog
        event.preventDefault();
        event.returnValue = ''; // Modern browsers ignore custom messages
        if (onNavigationAttempt) {
          onNavigationAttempt();
        }
        return ''; // Return empty string for modern browsers
      }
    };

    if (hasUnsavedWork) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedWork, onNavigationAttempt]);

  // Handle browser back/forward button with global handler
  useEffect(() => {
    const setupGlobalPopstateHandler = () => {
      if (!isGlobalHandlerAttached) {
        globalPopstateHandler = async (event: PopStateEvent) => {
          if (navigationWarningManager.checkActiveJobs() && !isNavigatingAfterConfirmation) {
            // Prevent the default navigation first
            event.preventDefault();
            
            const confirmed = await NavigationConfirmation.confirm();
            if (!confirmed) {
              // User cancelled - push current state back to stay on page
              window.history.pushState(null, '', window.location.pathname);
              return;
            } else {
              // User confirmed - allow navigation by going back
              isNavigatingAfterConfirmation = true;
              window.history.back();
              // Reset flag after a short delay
              setTimeout(() => {
                isNavigatingAfterConfirmation = false;
              }, 100);
            }
          }
        };

        window.addEventListener('popstate', globalPopstateHandler);
        isGlobalHandlerAttached = true;
      }
    };

    const cleanupGlobalPopstateHandler = () => {
      if (isGlobalHandlerAttached && globalPopstateHandler) {
        window.removeEventListener('popstate', globalPopstateHandler);
        isGlobalHandlerAttached = false;
        globalPopstateHandler = null;
      }
    };

    if (hasUnsavedWork) {
      // Push current state to enable popstate detection
      window.history.pushState(null, '', window.location.pathname);
      setupGlobalPopstateHandler();
    }

    // Don't cleanup here - let the navigation manager handle cleanup
    return () => {
      // Only cleanup if this is the last component with unsaved work
      if (!navigationWarningManager.checkActiveJobs()) {
        cleanupGlobalPopstateHandler();
      }
    };
  }, [hasUnsavedWork, onNavigationAttempt]);

  // Return a wrapped navigate function that shows warning before navigation
  const navigateWithWarning = useCallback(async (to: string | number, options?: any) => {
    if (hasUnsavedWork && !isNavigatingRef.current) {
      const confirmed = await NavigationConfirmation.confirm();
      if (confirmed) {
        isNavigatingRef.current = true;
        if (typeof to === 'string') {
          navigate(to, options);
        } else {
          navigate(to);
        }
      }
      if (onNavigationAttempt) {
        onNavigationAttempt();
      }
    } else {
      if (typeof to === 'string') {
        navigate(to, options);
      } else {
        navigate(to);
      }
    }
  }, [hasUnsavedWork, navigate, onNavigationAttempt]);

  // Function to allow navigation without warning (for form submissions, etc.)
  const allowNavigation = useCallback(() => {
    isNavigatingRef.current = true;
  }, []);

  // Function to block navigation again
  const blockNavigation = useCallback(() => {
    isNavigatingRef.current = false;
  }, []);

  return {
    navigateWithWarning,
    allowNavigation,
    blockNavigation
  };
};

/**
 * Context for managing navigation warnings across the app
 */
export interface NavigationWarningContextType {
  registerPage: (pageId: string, hasUnsavedWork: boolean) => void;
  unregisterPage: (pageId: string) => void;
  checkActiveJobs: () => boolean;
  getActivePages: () => string[];
}

// Global state to track pages with active work
let activePagesWithWork = new Map<string, { hasUnsavedWork: boolean }>();
let globalPopstateHandler: ((event: PopStateEvent) => void) | null = null;
let isGlobalHandlerAttached = false;
let isNavigatingAfterConfirmation = false;

export const navigationWarningManager = {
  registerPage: (pageId: string, hasUnsavedWork: boolean) => {
    if (hasUnsavedWork) {
      activePagesWithWork.set(pageId, {
        hasUnsavedWork
      });
    } else {
      activePagesWithWork.delete(pageId);
    }
  },

  unregisterPage: (pageId: string) => {
    activePagesWithWork.delete(pageId);
    
    // Clean up global popstate handler if no pages have active work
    if (!navigationWarningManager.checkActiveJobs() && isGlobalHandlerAttached && globalPopstateHandler) {
      window.removeEventListener('popstate', globalPopstateHandler);
      isGlobalHandlerAttached = false;
      globalPopstateHandler = null;
    }
  },

  checkActiveJobs: (): boolean => {
    let hasActiveJobs = false;
    activePagesWithWork.forEach((pageData) => {
      if (pageData.hasUnsavedWork) {
        hasActiveJobs = true;
      }
    });
    return hasActiveJobs;
  },

  getActivePages: (): string[] => {
    const activePages: string[] = [];
    activePagesWithWork.forEach((pageData, pageId) => {
      if (pageData.hasUnsavedWork) {
        activePages.push(pageId);
      }
    });
    return activePages;
  },

  showNavigationWarning: async (): Promise<boolean> => {
    if (navigationWarningManager.checkActiveJobs()) {
      const confirmed = await NavigationConfirmation.confirm();
      if (confirmed) {
        isNavigatingAfterConfirmation = true;
        // Reset flag after navigation completes
        setTimeout(() => {
          isNavigatingAfterConfirmation = false;
        }, 1000);
      }
      return confirmed;
    }
    return true;
  }
};

export default useNavigationWarning;